import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        throw new Error(result.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submit error:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Kunne ikke sende formularen. Ring venligst direkte på tlf. 60 71 50 18.'
      });
    }
  };

  return (
    <>
      <SEO 
        title="Kontakt os – Få et uforpligtende tilbud" 
        description="Kontakt Fyns - Tømreren ApS i Munkebo. Ring på 60 71 50 18 eller udfyld formularen for hurtig respons og et gennemskueligt tilbud på dit tømrerarbejde på Fyn." 
      />

      {/* Header */}
      <section className="bg-[#1F2A37] text-white py-16 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Kontakt Fyns - Tømreren ApS
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-base leading-relaxed">
            Har du et projekt i tankerne eller ønsker du et uforpligtende tilbud? Hos <Link to="/" className="text-white hover:text-gray-200 underline decoration-1 underline-offset-2 font-semibold" title="Gå til forsiden for Fyns Tømreren">Fyns - Tømreren ApS</Link> sidder vi klar til at besvare dine spørgsmål og yde faglig rådgivning.
          </p>
        </div>
      </section>

      {/* Main Section */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Contact Details Column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#111111] mb-2">
                  Direkte kontaktoplysninger
                </h2>
                <p className="text-gray-600 text-sm">
                  Du er altid velkommen til at ringe eller sende en mail til os.
                </p>
              </div>

              <div className="space-y-6">
                
                {/* Telefon */}
                <div className="flex items-start gap-4">
                  <div className="text-[#3A5A40] text-2xl flex-shrink-0 mt-1">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <a 
                      href="tel:60715018" 
                      className="text-lg font-bold text-[#111111] hover:text-[#3A5A40] transition-colors whitespace-nowrap"
                    >
                      60 71 50 18
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5">Ring for en hurtig snak om dit projekt</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="text-[#3A5A40] text-2xl flex-shrink-0 mt-1">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <a 
                      href="mailto:fynstomreren@gmail.com" 
                      className="text-base font-bold text-[#111111] hover:text-[#3A5A40] transition-colors whitespace-nowrap"
                    >
                      fynstomreren@gmail.com
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5">Vi besvarer e-mails hurtigst muligt</p>
                  </div>
                </div>

                {/* Adresse */}
                <div className="flex items-start gap-4">
                  <div className="text-[#3A5A40] text-2xl flex-shrink-0 mt-1">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <p className="text-base font-bold text-[#111111]">
                      Fjordvej 103, 5330 Munkebo
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">CVR: 34704201 • Dækker hele Fyn</p>
                  </div>
                </div>

                {/* Åbningstider */}
                <div className="flex items-start gap-4 pt-2 border-t border-gray-200">
                  <div className="text-[#3A5A40] text-2xl flex-shrink-0 mt-1">
                    <i className="fa-solid fa-clock"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">
                      Mandag - Fredag: 07:00 - 16:30
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Lørdag - Søndag: Efter aftale</p>
                  </div>
                </div>

                {/* Servicebil Photo Card */}
                <div className="pt-4">
                  <figure className="relative overflow-hidden border border-gray-200 shadow-sm bg-white">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/fyns-toemreren-firmabil-byggeplads.jpg"
                      alt="Fyns-Tømreren firmabil fotograferet på byggeplads i Munkebo på Fyn"
                      className="w-full h-48 object-cover"
                    />
                    <figcaption className="p-3 bg-gray-50 text-xs text-gray-600 border-t border-gray-100 flex items-center gap-2">
                      <i className="fa-solid fa-truck text-[#3A5A40]"></i>
                      <span>Fyns - Tømreren firmabil – Vi kører ud på hele Fyn fra vores base i Munkebo</span>
                    </figcaption>
                  </figure>
                </div>

              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7 bg-white p-8 sm:p-10 shadow-sm border border-gray-200">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#111111] mb-2">
                  Send en besked eller indhent tilbud
                </h2>
                <p className="text-gray-600 text-sm">
                  Udfyld formularen nedenfor, så vender vi tilbage med rådgivning og et uforpligtende tilbud.
                </p>
              </div>

              {status.success && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-lg mt-0.5"></i>
                  <div>
                    <p className="font-bold">Mange tak for din henvendelse!</p>
                    <p className="text-emerald-700 mt-1">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                  </div>
                </div>
              )}

              {status.error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
                  <i className="fa-solid fa-circle-exclamation text-red-600 text-lg mt-0.5"></i>
                  <div>
                    <p className="font-bold">Der opstod en fejl</p>
                    <p className="text-red-700 mt-1">{status.error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* ANTISPAM HONEYPOT - Hidden from human users via sr-only + absolute */}
                <div className="sr-only absolute opacity-0 pointer-events-none" aria-hidden="true">
                  <label htmlFor="website_url">Lad dette felt være tomt</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Navn */}
                  <div>
                    <label htmlFor="navn" className="block text-xs font-semibold uppercase text-gray-700 mb-1">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="navn"
                      name="navn"
                      required
                      placeholder="Dit fulde navn"
                      value={formData.navn}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:border-transparent text-sm text-[#111111]"
                    />
                  </div>

                  {/* Telefonnummer */}
                  <div>
                    <label htmlFor="telefon" className="block text-xs font-semibold uppercase text-gray-700 mb-1">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      required
                      placeholder="Tlf. nummer"
                      value={formData.telefon}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:border-transparent text-sm text-[#111111]"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase text-gray-700 mb-1">
                    E-mailadresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="din.email@adresse.dk"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:border-transparent text-sm text-[#111111]"
                  />
                </div>

                {/* Beskrivelse */}
                <div>
                  <label htmlFor="beskrivelse" className="block text-xs font-semibold uppercase text-gray-700 mb-1">
                    Projektbeskrivelse <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="beskrivelse"
                    name="beskrivelse"
                    rows={5}
                    required
                    placeholder="Beskriv kort dit ønske eller projekt (f.eks. nyt tag, sildebensparket, køkkenmontering osv.)"
                    value={formData.beskrivelse}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:border-transparent text-sm text-[#111111] resize-y"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="w-full bg-[#d75d5a] hover:bg-[#c24b48] text-white py-4 px-6 font-semibold text-base transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {status.submitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        Sender besked...
                      </>
                    ) : (
                      <>
                        Indsend tilbudsforespørgsel
                        <i className="fa-solid fa-paper-plane text-sm"></i>
                      </>
                    )}
                  </button>
                </div>

              </form>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
